import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Icon from './Icon.jsx';
import ShareCard from './ShareCard.jsx';
import { QUESTIONS } from '../data/characterQuiz.js';
import { alsoLike, scoreQuiz } from '../lib/characterQuiz.js';
import { playCorrect } from '../lib/sfx.js';

/**
 * Which character are you.
 *
 * Ten questions, one at a time, then a reveal. Built to be shared, which
 * decides most of what follows: no account, no paywall, no sign-in wall in
 * front of the result. A quiz that asks who you are before it tells you is a
 * quiz nobody sends to a friend.
 *
 * ── The shape of it ─────────────────────────────────────────────────────────
 *
 * Choosing advances on its own after a short pause. The pause is not decoration
 * — it is long enough to see the answer take, short enough that ten of them do
 * not feel like a form. A Next button here would add ten taps to a ninety-second
 * experience for no information.
 *
 * The reveal holds for a beat before naming anyone. Cutting straight from the
 * last tap to the answer reads as a lookup; a moment of the app apparently
 * thinking reads as a verdict, and the verdict is the thing people screenshot.
 * Under `prefers-reduced-motion` the whole sequence collapses to an instant
 * change of screen, because a held pause with no animation is just a delay.
 *
 * The only thing it writes is the one-off XP award, fired the moment the last
 * answer lands rather than when the reveal finishes — somebody who closes the
 * tab during the pause has still answered ten questions. The result itself
 * lives in component state and is gone on reload, which is the right trade for
 * something whose whole job is to be taken and passed on.
 */
/**
 * @param takes  completed takes on this device, including the one just finished
 * @param onNeedAccount  open the sign-up sheet
 * @param onUpgrade      open the paywall, quietly and second
 */
export default function CharacterQuiz({
  onClose,
  onSignIn,
  signedIn,
  isPremium = false,
  takes = 0,
  onNeedAccount,
  onUpgrade,
  onComplete,
}) {
  const [answers, setAnswers] = useState(() => QUESTIONS.map(() => null));
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState('asking'); // asking | reading | result
  const [sharing, setSharing] = useState(false);
  const advanceTimer = useRef(null);
  const readTimer = useRef(null);

  const reduced =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => () => {
    clearTimeout(advanceTimer.current);
    clearTimeout(readTimer.current);
  }, []);

  const question = QUESTIONS[index];
  const chosen = answers[index];
  const result = useMemo(() => scoreQuiz(answers), [answers]);
  // One complete take, result and share included, then an account to go again.
  const gateRetake = !signedIn && takes >= 1;
  const second = useMemo(() => (phase === 'result' ? alsoLike(result) : null), [phase, result]);

  const choose = useCallback(
    (optionIndex) => {
      if (chosen !== null) return; // one tap per question; the pause is not a window to change it
      const next = [...answers];
      next[index] = optionIndex;
      setAnswers(next);

      const last = index === QUESTIONS.length - 1;
      advanceTimer.current = setTimeout(
        () => {
          if (!last) {
            setIndex((i) => i + 1);
            return;
          }
          playCorrect();
          setPhase('reading');
          // Scored from `next` rather than from state: setAnswers has not
          // committed yet at this point in the same tick.
          onComplete?.(scoreQuiz(next).id);
          readTimer.current = setTimeout(() => setPhase('result'), reduced ? 0 : 2100);
        },
        reduced ? 0 : 420,
      );
    },
    [answers, chosen, index, reduced, onComplete],
  );

  const back = useCallback(() => {
    if (index === 0) return;
    clearTimeout(advanceTimer.current);
    // Clear the answer being returned to, or the question reopens already
    // answered and immediately advances again.
    setAnswers((current) => {
      const next = [...current];
      next[index - 1] = null;
      return next;
    });
    setIndex((i) => i - 1);
  }, [index]);

  const restart = useCallback(() => {
    setAnswers(QUESTIONS.map(() => null));
    setIndex(0);
    setPhase('asking');
  }, []);

  const progress = ((index + (chosen !== null ? 1 : 0)) / QUESTIONS.length) * 100;

  // ── The reveal ────────────────────────────────────────────────────────────

  if (phase === 'reading') {
    return (
      <section className="screen quiz quiz--reading">
        <div className="quiz-reading">
          <span className="quiz-reading__mark" aria-hidden="true">
            <Icon name="cross" size={34} />
          </span>
          <p className="quiz-reading__text" role="status">
            Reading your answers…
          </p>
        </div>
      </section>
    );
  }

  if (phase === 'result') {
    const c = result.character;
    return (
      <section className="screen quiz quiz--result">
        <div className="quiz__bar">
          <button type="button" className="icon-btn icon-btn--sm" onClick={onClose}>
            <Icon name="arrow" size={15} className="icon--flip" />
            Done
          </button>
        </div>

        <div className="reveal">
          <p className="reveal__eyebrow">You are most like</p>
          <div className="reveal__glyph" aria-hidden="true">
            {c.emoji}
          </div>
          <h2 className="reveal__name">{c.name}</h2>
          <p className="reveal__tagline">{c.tagline}</p>

          <blockquote className="reveal__verse">
            <p>{c.verse.text}</p>
            <cite>{c.verse.ref}</cite>
          </blockquote>

          <p className="reveal__body">{c.body}</p>

          <div className="reveal__note">
            <span className="reveal__note-label">What this reveals</span>
            <p>{c.reveals}</p>
          </div>

          {second ? (
            <p className="reveal__also">
              There is a lot of {second.name} {second.emoji} in you too — the two were
              close.
            </p>
          ) : null}

          {c.support ? <p className="reveal__support">{c.support}</p> : null}

          <div className="reveal__actions">
            <button
              type="button"
              className="btn btn--primary btn--block btn--lg"
              onClick={() => setSharing(true)}
            >
              <Icon name="ribbon" size={16} />
              Share your result
            </button>
            {/* Sharing stays first and stays free for everyone: it is how the
                next person arrives, and putting a gate in front of it would
                cost more than it collects. */}
            {gateRetake ? (
              <button
                type="button"
                className="btn btn--ghost btn--block"
                onClick={onNeedAccount}
              >
                Take it again — free account
              </button>
            ) : (
              <button type="button" className="btn btn--ghost btn--block" onClick={restart}>
                Take it again
              </button>
            )}
          </div>

          {/*
            The old line here promised "read {name}'s story in full". There is
            no per-character story in this app — the chapters are thematic —
            so it sent people to something that does not exist. What an account
            genuinely does is keep this: the result, the verse, the streak.
          */}
          {!signedIn ? (
            <button type="button" className="reveal__signin" onClick={onNeedAccount ?? onSignIn}>
              Keep {c.name} as your result — on every device, not just this browser
            </button>
          ) : null}

          {/* One quiet line, below the account ask, never above it. */}
          {!isPremium ? (
            <button type="button" className="reveal__premium" onClick={onUpgrade}>
              Premium opens all 28 chapters of the story, and every verse
            </button>
          ) : null}
        </div>

        {sharing ? (
          <ShareCard character={c} onClose={() => setSharing(false)} />
        ) : null}
      </section>
    );
  }

  // ── The questions ─────────────────────────────────────────────────────────

  return (
    <section className="screen quiz">
      <div className="quiz__bar">
        <button type="button" className="icon-btn icon-btn--sm" onClick={onClose}>
          <Icon name="arrow" size={15} className="icon--flip" />
          Leave
        </button>
        <span className="quiz__count">
          {index + 1} of {QUESTIONS.length}
        </span>
      </div>

      <div className="quiz-progress">
        <div className="quiz-progress__fill" style={{ width: `${progress}%` }} />
      </div>

      {/* Keyed on the question so React remounts it and the slide-in replays. */}
      <div className="quiz-card" key={question.id}>
        <h2 className="quiz-card__question">{question.text}</h2>

        <ul className="quiz-options">
          {question.options.map((option, i) => (
            <li key={option.text}>
              <button
                type="button"
                className={`quiz-option${chosen === i ? ' is-chosen' : ''}${
                  chosen !== null && chosen !== i ? ' is-dim' : ''
                }`}
                onClick={() => choose(i)}
                disabled={chosen !== null}
              >
                <span className="quiz-option__text">{option.text}</span>
                <span className="quiz-option__tick" aria-hidden="true">
                  {chosen === i ? <Icon name="check" size={15} /> : null}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="quiz__foot">
        {index > 0 ? (
          <button type="button" className="btn btn--ghost" onClick={back}>
            Back
          </button>
        ) : (
          <p className="quiz__hint">Answer honestly — nobody sees this but you.</p>
        )}
      </div>
    </section>
  );
}
